import { getAppConfig, resolveApiServer } from './lib/runtimeConfig';
import { ROLES } from './types';

// Runtime config (public/config.js) wins; falls back to build-time env for
// `npm run dev` and tests. Lets one build serve any environment.
const server = resolveApiServer(getAppConfig(), import.meta.env);

/**
 * Route pathname constants for string comparisons only.
 *
 * Note: For navigation, use TanStack Router's type-safe `to` parameter directly.
 * These constants are only needed for pathname string comparisons like:
 * - window.location.pathname !== ROUTE_PATHS.LOGIN
 * - router.state.location.pathname !== ROUTE_PATHS.LOGIN
 *
 * TanStack Router provides type safety for navigation through the generated
 * route tree, so prefer using the typed `to` parameter in navigate() and redirect().
 */
const ROUTE_PATHS = {
  LOGIN: '/login',
  RESET_PASSWORD: '/reset-password',
} as const;

const publicRoutes: string[] = [ROUTE_PATHS.RESET_PASSWORD, ROUTE_PATHS.LOGIN];

// Single source of truth for public-route membership, so beforeLoad, the auth gate, the sidebar
// and useAuthStatus can't drift. Slash/case normalized because routeTree.gen.ts, ROUTE_PATHS and
// TanStack Router's case-insensitive matching disagree on the spelling.
function normalizePath(pathname: string): string {
  return pathname.toLowerCase().replace(/\/+$/, '') || '/';
}

function isPublicRoute(pathname: string): boolean {
  return publicRoutes.includes(normalizePath(pathname));
}

// The post-login callback window, used by every site that relaxes an auth check for it. Scoped to
// "/" (portal-backend only redirects to an origin root) and parsed, not substring-matched: these
// checks suppress redirects, so `?next=/x?login=success` must not satisfy them.
function isPostLoginCallback(pathname: string, search: string): boolean {
  return normalizePath(pathname) === '/' && new URLSearchParams(search).get('login') === 'success';
}

/**
 * Shared React Query cache timing for organizations-related queries.
 *
 * - ORGS_QUERY_STALE_TIME: How long organization data is considered fresh (ms)
 * - ORGS_QUERY_GC_TIME: How long unused organization data stays in cache (ms)
 *
 * Organizations change infrequently, so we use long cache times.
 */
export const ORGS_QUERY_STALE_TIME = 30 * 24 * 60 * 60 * 1000; // 30 days
export const ORGS_QUERY_GC_TIME = 60 * 24 * 60 * 60 * 1000; // 60 days

/**
 * Shared React Query cache timing for studies-related queries.
 *
 * - STUDIES_QUERY_STALE_TIME: How long studies data is considered fresh (ms)
 * - STUDIES_QUERY_GC_TIME: How long unused studies data stays in cache (ms)
 *
 * Set longer for studies-heavy navigation UX.
 */
export const STUDIES_QUERY_STALE_TIME = 30 * 60 * 1000; // 30 minutes
export const STUDIES_QUERY_GC_TIME = 60 * 60 * 1000; // 60 minutes

/**
 * Shared React Query cache timing for instrument queries.
 * Instruments change infrequently (device fleet management), so we use moderate cache times.
 */
export const INSTRUMENTS_QUERY_STALE_TIME = 5 * 60 * 1000; // 5 minutes
export const INSTRUMENTS_QUERY_GC_TIME = 30 * 60 * 1000; // 30 minutes

/**
 * Shared React Query cache timing for instrument run queries.
 *
 * Instrument runs have three statuses with very different update frequencies:
 *
 * - DRAFT      — actively edited by the user; stale immediately so the form
 *                always loads the latest saved state.
 * - SUBMITTED  — being processed by the instrument; status/progress can change
 *                at any time, so keep the window short.
 * - COMPLETED  — immutable once done; safe to cache for a long time.
 *
 * The loader for /instrument_runs/new uses DRAFT_STALE_TIME for its fetchQuery
 * so that every visit to the edit form fetches fresh data.
 * The detail route ($instrumentRunId) uses COMPLETED_STALE_TIME for the cache
 * entry written after a successful load of a non-draft run.
 */
export const INSTRUMENT_RUN_DRAFT_STALE_TIME = 0; // always fresh — actively edited
export const INSTRUMENT_RUN_SUBMITTED_STALE_TIME = 30 * 1000; // 30 seconds — in processing
export const INSTRUMENT_RUN_COMPLETED_STALE_TIME = 30 * 60 * 1000; // 30 minutes — immutable
export const INSTRUMENT_RUN_GC_TIME = 60 * 60 * 1000; // 60 minutes — consistent with studies

// No push/websocket channel for decode progress — poll while a run is active
// (see `isInstrumentRunActive` in `pages/Instrument_Runs/runStatus.ts`).
export const INSTRUMENT_RUN_ACTIVE_POLL_INTERVAL_MS = 15 * 1000;

/**
 * Pagination settings for sample proteoform tables
 */
export const SAMPLE_PROTEOFORMS_PAGE_SIZE = 24;
export const SAMPLE_PROTEOFORMS_PAGE_SIZE_OPTIONS = [4, 12, 24];

/** Standard page size options for AG Grid tables across the portal. */
export const TABLE_PAGE_SIZE_OPTIONS = [10, 25, 50, 100];

/**
 * Proteoform query configuration
 */
export const PROTEOFORM_QUERY_LIMIT = 500;
/**
 * Abundance floor in cpm. Optional on the wire (the server defaults to 1000) but the generated
 * type marks it required, and it is sent explicitly regardless: it changes both which rows come
 * back AND the header counts, so leaving it implicit would make those numbers hard to reason about.
 */
export const PROTEOFORM_DEFAULT_THRESHOLD_CPM = 1000;
export const PROTEOFORM_DEFAULT_SORT_BY = 'sample_name' as const;
export const PROTEOFORM_DEFAULT_SORT_ORDER = 'asc' as const;
export const PLACEHOLDER_VALUE = '-';

/**
 * Post-login session polling configuration.
 *
 * After a redirect-based login the server may not yet have set the session
 * cookie by the time the router runs. We poll /auth/me until it returns 200
 * or until MAX_ATTEMPTS is exhausted.
 *
 * - RETRY_INTERVAL_MS:      delay between each poll attempt
 * - MAX_ATTEMPTS:           base attempt ceiling (~14 s total)
 * - MAX_ATTEMPTS_EXTENDED:  extended ceiling used when a recent logout is
 *                           detected (~28 s total), to allow for quick re-logins
 * - RECENT_LOGOUT_WINDOW_MS: window within which a logout is considered recent
 * - NAV_LOCK_DURATION_MS:   how long the nav lock is held after a successful
 *                           post-login org selection to prevent competing redirects
 */
export const POST_LOGIN_POLL_INTERVAL_MS = 700;
export const POST_LOGIN_MAX_ATTEMPTS = 20;
export const POST_LOGIN_MAX_ATTEMPTS_EXTENDED = 40;
export const RECENT_LOGOUT_WINDOW_MS = 10_000;
export const NAV_LOCK_DURATION_MS = 10_000;

// How long after a logout an auth redirect / revalidation stays suppressed. Distinct from
// RECENT_LOGOUT_WINDOW_MS above, which extends post-login polling rather than suppressing anything.
export const RECENT_LOGOUT_SUPPRESSION_MS = 15_000;

// The auth query sets retry: false and refetches on a 5-minute interval, so a session cookie that
// lands just after the first probe would otherwise sit behind the gate until the watchdog gave up.
// The gate re-probes on this interval so it can open itself.
export const INITIAL_AUTH_GATE_RECHECK_MS = 2_000;

// Once the watchdog has given up, the user has an explicit Try Again and the tight loop's only
// remaining job is to catch a late recovery — so it backs off here rather than polling an already
// failing /auth/me at 0.5 req/s from every open tab for as long as the tab stays open.
export const INITIAL_AUTH_GATE_IDLE_RECHECK_MS = 30_000;

// Watchdog for __root.lazy.tsx's initial auth gate (POR-433), or a race that never reaches
// AUTHENTICATED leaves a permanent spinner. A nominal probe count, not an enforced one — the
// watchdog is its own setTimeout. Long on purpose: see SESSION_EXPIRATION_FIX.md.
export const INITIAL_AUTH_GATE_MAX_PROBES = 15;
export const INITIAL_AUTH_GATE_TIMEOUT_MS = INITIAL_AUTH_GATE_RECHECK_MS * INITIAL_AUTH_GATE_MAX_PROBES;

// Watchdog for useOrgAdminRenderGate's no-access states (issues #396/#397): `denied` awaits
// useRouteProtection's redirect, `unauthenticated` the root beforeLoad's /login, and router.navigate's
// rate-limit guard can drop either silently. Short on purpose — a real redirect lands within one tick.
export const ORG_ADMIN_GATE_REDIRECT_TIMEOUT_MS = 5_000;

// How long useRouteProtection keeps an established session's access alive while /auth/me is
// failing. Deliberately shorter than AUTH_REFETCH_INTERVAL_MS: a mask outliving the poll that
// could lift it is indefinite in practice, and an unrecoverable session must reach the
// AuthCheckFailed retry surface rather than sitting on a stale page forever.
export const AUTH_ME_FAILURE_MASK_MAX_MS = 2 * 60 * 1000;

/**
 * Default column definition settings
 */
const defaultColDef = {
  resizable: true,
  sortable: true,
  filter: true,
};

const adminRoles = [ROLES[0], ROLES[1]];

// Standardized icon size for main icons across the application
const mainIconSize = 20;
const smIconSize = 14;

// publicRoutes stays module-private: exporting the bare list invites the exact-match check that
// isPublicRoute exists to replace.
export { adminRoles, defaultColDef, isPostLoginCallback, isPublicRoute, mainIconSize, ROUTE_PATHS, server, smIconSize };
